import { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "@/src/services/api";
import { markIndexCachesStale } from "@/utils/localStore";
import { trackBackendHit, trackVersionCheck } from "@/utils/trafficTracker";
const DATA_VERSION_KEY_PREFIX = "dataVersion:";

// ── Shared SSE connection (single connection for all hook instances) ──
let eventSource = null;
let sseListeners = [];
let sseConnected = false;
let sseRetryTimer = null;
function connectSSE() {
  if (eventSource) return;
  if (typeof window === "undefined" || typeof EventSource === "undefined") return;
  const url = `${API_BASE_URL}/data-versions/stream`;
  eventSource = new EventSource(url);
  sseConnected = false;
  eventSource.onopen = () => {
    sseConnected = true;
    if (sseRetryTimer) {
      clearTimeout(sseRetryTimer);
      sseRetryTimer = null;
    }
  };
  eventSource.onmessage = msg => {
    try {
      const data = JSON.parse(msg.data);
      if (data.entityType && typeof data.version === "number") {
        sseListeners.forEach(fn => fn(data));
      }
    } catch {
      // Ignore malformed messages
    }
  };
  eventSource.onerror = () => {
    sseConnected = false;
    eventSource?.close();
    eventSource = null;
    // Retry after 30 seconds
    if (!sseRetryTimer) {
      sseRetryTimer = setTimeout(() => {
        sseRetryTimer = null;
        if (sseListeners.length > 0) connectSSE();
      }, 30_000);
    }
  };
}
function subscribeSSE(listener) {
  sseListeners.push(listener);
  if (!eventSource && !isTabHidden()) connectSSE();
  return () => {
    sseListeners = sseListeners.filter(fn => fn !== listener);
    if (sseListeners.length === 0 && eventSource) {
      eventSource.close();
      eventSource = null;
      sseConnected = false;
      if (sseRetryTimer) {
        clearTimeout(sseRetryTimer);
        sseRetryTimer = null;
      }
    }
  };
}

// ── Visibility-aware SSE management ──
// Disconnect SSE when tab is hidden to avoid background connections;
// reconnect when tab becomes visible again.
function isTabHidden() {
  return typeof document !== "undefined" && document.hidden;
}
function disconnectSSE() {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
    sseConnected = false;
  }
  if (sseRetryTimer) {
    clearTimeout(sseRetryTimer);
    sseRetryTimer = null;
  }
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      disconnectSSE();
    } else {
      // Bumps that fired while the tab was hidden were missed (SSE
      // was disconnected) and the 15-min token would keep answering
      // "fresh" — force the next check to hit the network.
      lastFetchTime = 0;
      cachedVersions = null;
      if (sseListeners.length > 0) {
        connectSSE();
      }
    }
  });
}

// ── Fallback: fetch-based version check (used on mount + when SSE is down) ──
let cachedVersions = null;
let fetchPromise = null;
let lastFetchTime = 0;

/**
 * How long a fetched /data-versions snapshot stays authoritative.
 *
 * The snapshot is persisted to localStorage as an expiring token so FULL page
 * loads within the window serve entirely from cache — zero backend hits for
 * version checks — instead of re-fetching on every mount. When the token
 * expires, the next mount fetches once: an unchanged version simply renews
 * the token for another window; a bumped version invalidates the local
 * caches. SSE bumps (when connected) still land in real time and are patched
 * into the token, so "at most 15 minutes stale" is the worst case, not the
 * norm. The admin "Refresh Data" action wipes every pc_* key, including this
 * token, forcing a fresh check for all entities.
 */
const VERSIONS_TTL_MS = 15 * 60_000;
const VERSIONS_TOKEN_KEY = "pc_data_versions_token_v1";
function readVersionsToken() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(VERSIONS_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.expiresAt !== "number" || !parsed.versions || typeof parsed.versions !== "object") {
      return null;
    }
    if (Date.now() >= parsed.expiresAt) {
      // Drop the stale entry so future mounts skip the parse entirely.
      localStorage.removeItem(VERSIONS_TOKEN_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
function writeVersionsToken(versions) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(VERSIONS_TOKEN_KEY, JSON.stringify({
      versions,
      expiresAt: Date.now() + VERSIONS_TTL_MS
    }));
  } catch {
    // storage quota — the fetch fallback still works without the token
  }
}

/** Keep the token truthful when SSE pushes a bump mid-window. */
function patchVersionsToken(entityType, version) {
  const token = readVersionsToken();
  if (!token) return;
  try {
    localStorage.setItem(VERSIONS_TOKEN_KEY, JSON.stringify({
      versions: {
        ...token.versions,
        [entityType]: version
      },
      expiresAt: token.expiresAt
    }));
  } catch {
    // ignore
  }
}

/** Where a versions snapshot came from — kept truthful so the traffic
 *  tracker can distinguish "compared against the cached token" (free)
 *  from "compared against a real /data-versions fetch" (a network hit). */

async function fetchVersions() {
  const now = Date.now();
  if (cachedVersions && now - lastFetchTime < VERSIONS_TTL_MS) {
    return {
      versions: cachedVersions,
      source: "memory"
    };
  }
  // A fresh full page load starts with empty module state — consult the
  // persisted token before going to the network.
  const token = readVersionsToken();
  if (token) {
    cachedVersions = token.versions;
    // Align the in-memory window with the token's remaining lifetime.
    lastFetchTime = token.expiresAt - VERSIONS_TTL_MS;
    return {
      versions: token.versions,
      source: "token"
    };
  }
  if (fetchPromise) return fetchPromise;
  fetchPromise = (async () => {
    const started = typeof performance !== "undefined" ? performance.now() : Date.now();
    try {
      const res = await fetch(`${API_BASE_URL}/data-versions`, {
        credentials: "include"
      });
      const ended = typeof performance !== "undefined" ? performance.now() : Date.now();
      trackBackendHit({
        endpoint: "/data-versions",
        method: "GET",
        status: res.status,
        durationMs: Math.round(ended - started),
        source: "version-fetch"
      });
      if (!res.ok) return {
        versions: cachedVersions ?? {},
        source: "network"
      };
      const data = await res.json();
      cachedVersions = data;
      lastFetchTime = Date.now();
      // Persist as an expiring token: full page loads within the next
      // 15 minutes serve from localStorage with zero backend hits.
      writeVersionsToken(data);
      return {
        versions: data,
        source: "network"
      };
    } catch {
      trackBackendHit({
        endpoint: "/data-versions",
        method: "GET",
        source: "version-fetch",
        detail: {
          error: true
        }
      });
      return {
        versions: cachedVersions ?? {},
        source: "network"
      };
    } finally {
      fetchPromise = null;
    }
  })();
  return fetchPromise;
}

// ── Deduped version-check logging ──
//
// Several components mount version checks for the same entity on one page
// (the page itself, Navigation, featured sections), all comparing the same
// stored/server pair. Emitting one tracker row per hook instance made the
// analytics read as N checks (and, worse, N "version-fetch" rows for zero
// or one actual network request). Identical results within a short window
// on the same pathname are logged once.
const recentVersionLogs = new Map();
const VERSION_LOG_DEDUPE_MS = 1500;
function trackVersionCheckDeduped(args) {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const key = [path, args.entity, args.storedVersion, args.serverVersion, args.stale, args.source].join("|");
  const now = Date.now();
  const last = recentVersionLogs.get(key);
  if (last !== undefined && now - last < VERSION_LOG_DEDUPE_MS) return;
  if (recentVersionLogs.size > 200) recentVersionLogs.clear();
  recentVersionLogs.set(key, now);
  trackVersionCheck(args);
}

/**
 * Checks backend data version for a given entity type.
 *
 * Primary: SSE push — a single persistent connection receives version bumps
 * in real time when an admin approves content.
 *
 * Fallback: fetch-based check on mount, backed by a 15-minute expiring token
 * in localStorage — repeat page loads within the window are answered from
 * the token with zero backend requests; an expired token triggers ONE fetch
 * which renews it (see VERSIONS_TTL_MS). "Refresh Data" wipes the token.
 *
 * Returns `true` when the local cache was stale and has been invalidated,
 * signalling the caller to skip its cached-data shortcut.
 *
 * Optional `onStale` callback is invoked each time a stale version is
 * detected. Useful for dispatching RTK Query tag invalidations or other
 * non-localStorage cache busting (e.g. promotions).
 */
export function useDataVersionCheck(entityType, onStale) {
  const [stale, setStale] = useState(false);
  const checked = useRef(false);
  const onStaleRef = useRef(onStale);
  useEffect(() => {
    onStaleRef.current = onStale;
  });

  // ── Initial fetch-based check on mount ──
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (checked.current) return;
    checked.current = true;
    (async () => {
      const snapshot = await fetchVersions();
      const serverVersion = snapshot.versions[entityType];
      if (serverVersion === undefined) return;
      const storageKey = `${DATA_VERSION_KEY_PREFIX}${entityType}`;
      const storedRaw = localStorage.getItem(storageKey);
      const storedVersion = storedRaw !== null ? parseInt(storedRaw, 10) : null;
      const isStale = storedVersion === null || storedVersion !== serverVersion;
      trackVersionCheckDeduped({
        entity: entityType,
        storedVersion,
        serverVersion,
        stale: isStale,
        // "version-fetch" is reserved for comparisons backed by a
        // real network request this page load; token/memory serves
        // are free and labeled as such.
        source: snapshot.source === "network" ? "version-fetch" : snapshot.source
      });
      if (isStale) {
        // Bump local version FIRST so version-gated detail caches
        // start treating older entries as stale on the next read.
        localStorage.setItem(storageKey, String(serverVersion));
        if (!entityType.startsWith("org-content:")) {
          markIndexCachesStale(entityType);
        }
        onStaleRef.current?.();
        setStale(true);
      }
    })();
  }, [entityType]);

  // ── SSE listener for real-time version bumps ──
  useEffect(() => {
    if (typeof window === "undefined") return;
    const unsubscribe = subscribeSSE(event => {
      if (event.entityType !== entityType) return;
      const storageKey = `${DATA_VERSION_KEY_PREFIX}${entityType}`;
      const storedRaw = localStorage.getItem(storageKey);
      const storedVersion = storedRaw !== null ? parseInt(storedRaw, 10) : null;
      const isStale = storedVersion === null || storedVersion !== event.version;
      trackVersionCheckDeduped({
        entity: entityType,
        storedVersion,
        serverVersion: event.version,
        stale: isStale,
        source: "sse"
      });
      if (isStale) {
        localStorage.setItem(storageKey, String(event.version));
        if (!entityType.startsWith("org-content:")) {
          markIndexCachesStale(entityType);
        }
        onStaleRef.current?.();
        setStale(true);
        // Keep both caches truthful: patch the persisted token with
        // the pushed version (its window keeps running) and update
        // the in-memory snapshot in place.
        patchVersionsToken(entityType, event.version);
        if (cachedVersions) {
          cachedVersions = {
            ...cachedVersions,
            [entityType]: event.version
          };
        }
      }
    });
    return unsubscribe;
  }, [entityType]);
  return stale;
}